/* eslint-disable react-hooks/rules-of-hooks */
import { Button } from "@/components/tailwind-ui/Button";
import { Input } from "@/components/tailwind-ui/Input";
import { SectionHeading } from "@/components/tailwind-ui/SectionHeading";
import { Menu, Switch, Transition } from "@headlessui/react";
import cn from "classnames";
import { ArrowLeft, ArrowRight, DotsVertical, SortAscending, SortDescending, SwitchVertical } from "heroicons-react";
import { useRouter } from "next/router";
import React, { FC, useEffect, useRef, useState } from "react";
import {
  Column,
  ColumnInstance,
  HeaderGroup,
  Row,
  TableOptions,
  useBlockLayout,
  useFilters,
  UseFiltersColumnOptions,
  UseFiltersColumnProps,
  useSortBy,
  UseSortByColumnOptions,
  UseSortByColumnProps,
  useTable,
  usePagination,
} from "react-table";
import { FixedSizeList } from "react-window";

export const ActionsColumn = (Children) => ({
  Header: "Actions",
  accessor: () => null,
  width: 4.25 * 16,
  Cell: ({ row }) => {
    return (
      <Menu as="div" className="relative">
        {({ open }) => (
          <>
            <Menu.Button>
              <DotsVertical className="w-5 h-5 text-gray-400" />
            </Menu.Button>
            <Transition
              show={open}
              enter="transition ease-out duration-100"
              enterFrom="transform opacity-0 scale-95"
              enterTo="transform opacity-100 scale-100"
              leave="transition ease-in duration-75"
              leaveFrom="transform opacity-100 scale-100"
              leaveTo="transform opacity-0 scale-95"
            >
              <Menu.Items className="absolute left-0 z-20 w-56 space-y-1 origin-top-left bg-white border border-gray-200 divide-y divide-gray-100 rounded-md shadow-lg outline-none">
                <Children row={row} />
              </Menu.Items>
            </Transition>
          </>
        )}
      </Menu>
    );
  },
});

export const DefaultColumnFilter =
  (placeholder?: string) =>
  ({ column }) => {
    const { id, Header, filterValue, setFilter } = column;

    return (
      <Input
        className="h-8 rounded-none"
        label={Header}
        labelHidden
        name={id}
        value={filterValue || ""}
        onChange={(e) => {
          setFilter(e.target.value || undefined);
        }}
        placeholder={placeholder || `${Header} filtern`}
      />
    );
  };

const defaultColumn: any = {
  width: 16 * 16,
  Filter: DefaultColumnFilter(),
};

type ReactTableRowProps = {
  prepareRow: Function;
  row: Row<any>;
  style?: React.CSSProperties;
  isOpen?: boolean;
  SubComponent?: FC<any>;
  SubComponentWidth?: number;
};

const ReactTableRow: FC<ReactTableRowProps> = React.memo((props) => {
  const { prepareRow, row, style, isOpen, SubComponent, SubComponentWidth } = props;
  prepareRow(row);

  return (
    <>
      <tr {...row.getRowProps()} className="text-sm" style={style}>
        {row.cells.map((cell) => {
          if (["Actions", "Expander"].includes(cell.column.id)) {
            return (
              <td key={row.id} className="px-6 py-4">
                {cell.render("Cell")}
              </td>
            );
          }
          return (
            <td key={row.id} className="flex-shrink-0 px-6 py-4 truncate whitespace-nowrap" {...cell.getCellProps()}>
              {cell.render("Cell")}
            </td>
          );
        })}
      </tr>
      {isOpen && (
        <tr>
          <td colSpan={row.cells.length}>
            <div className="px-8 py-4" style={{ width: SubComponentWidth + "px" }}>
              <SubComponent {...row} />
            </div>
          </td>
        </tr>
      )}
    </>
  );
});

type ColumnToggleProps = {
  allColumns: Array<ColumnInstance<any>>;
};

const ColumnToggle: FC<ColumnToggleProps> = ({ allColumns }) => {
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  return (
    <div className="relative">
      <Button scheme="white" size="2" onClick={() => setIsFilterOpen((b) => !b)}>
        Spalten filtern
      </Button>
      <div
        className={cn("absolute p-4 bg-white border border-gray-200 shadow w-64 z-10 space-y-2", {
          hidden: !isFilterOpen,
        })}
      >
        {allColumns.map((column) => {
          if (["Actions", "Expander"].includes(column.id)) return null;
          return (
            <Switch.Group key={column.id} as="div" className="flex items-center space-x-4">
              <Switch
                as="button"
                checked={column.isVisible}
                onChange={(e) => column.toggleHidden(!e)}
                className={`${
                  column.isVisible ? "bg-primary" : "bg-gray-200"
                } relative inline-flex flex-shrink-0 h-6 transition-colors duration-200 ease-in-out border-2 border-transparent rounded-full cursor-pointer w-11 focus:outline-none focus:shadow-outline`}
              >
                <span className="sr-only">{column.Header}</span>
                <span
                  className={`${
                    column.isVisible ? "translate-x-5" : "translate-x-0"
                  } inline-block w-5 h-5 transition duration-200 ease-in-out transform bg-white rounded-full`}
                />
              </Switch>
              <Switch.Label>{column.Header}</Switch.Label>
            </Switch.Group>
          );
        })}
      </div>
    </div>
  );
};

export type ReactTableProps = {
  data: any;
  noDataComponent?: React.ReactElement;
  columns: Array<Column<any> & UseFiltersColumnOptions<any> & UseSortByColumnOptions<any>>;
  HeadActions?: React.ReactElement;
  allowColumnToggle?: boolean;
  showHeading?: boolean;
  withFilter?: boolean;
  virtualized?: boolean;
  headingTitle?: string;
  SubComponent?: FC<any>;
  initialState?: TableOptions<any>["initialState"];
};

const Pagination = (props) => {
  const {
    page,
    canPreviousPage,
    canNextPage,
    pageOptions,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    state: { pageIndex },
  } = props;

  return (
    <nav className="flex items-end justify-end px-4 py-2 border-b border-gray-200 sm:px-0">
      <div className="flex mr-4 -mt-px">
        <button
          className="inline-flex items-center pt-4 pr-1 text-sm font-medium text-gray-500 border-b-2 border-transparent outline-none hover:border-gray-300 hover:text-gray-700 focus:outline-none"
          onClick={() => {
            if (!canPreviousPage) return;
            previousPage();
          }}
          disabled={!canPreviousPage}
        >
          <ArrowLeft className="w-5 h-5 mr-3 text-gray-400" aria-hidden="true" />
          Vorherige Seite
        </button>
      </div>
      <div className="hidden md:-mt-px md:flex">
        {[...Array(pageCount - 1)].map((_, index) => {
          const isActive = pageIndex === index + 1;
          return (
            <button
              key={index}
              className={cn(
                "inline-flex items-center border-b-2  px-4 pt-4 text-sm font-medium  outline-none focus:outline-none",
                {
                  "border-indigo-500 text-indigo-600": isActive,
                  "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300": !isActive,
                }
              )}
              onClick={() => {
                gotoPage(index + 1);
              }}
            >
              {index + 1}
            </button>
          );
        })}
      </div>
      <div className="flex ml-4 -mt-px">
        <button
          className="inline-flex items-center pt-4 pl-1 text-sm font-medium text-gray-500 border-b-2 border-transparent outline-none hover:border-gray-300 hover:text-gray-700 focus:outline-none"
          onClick={() => {
            if (!canNextPage) return;
            nextPage();
          }}
          disabled={!canNextPage}
        >
          Nächste Seite
          <ArrowRight className="w-5 h-5 ml-3 text-gray-400" aria-hidden="true" />
        </button>
      </div>
    </nav>
  );
};

export const ReactTable: FC<ReactTableProps> = ({
  data,
  columns,
  HeadActions,
  allowColumnToggle,
  showHeading,
  withFilter,
  virtualized,
  headingTitle,
  SubComponent,
  noDataComponent,
  initialState,
}) => {
  if (typeof window === "undefined") return null;
  const hooks = [useFilters, useSortBy, ...(virtualized ? [useBlockLayout] : []), usePagination];
  const tableInstance = useTable(
    {
      columns,
      defaultColumn,
      data,
      initialState: {
        // @ts-expect-error Exists
        pageIndex: 1,
        pageSize: 200,
        ...initialState,
      },
    },
    ...hooks
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    allColumns,
    // @ts-expect-error Exists
    page,
  } = tableInstance;

  const [tableWidth, setTableWidth] = useState(0);
  const tableRef = useRef<HTMLTableElement>(null);
  useEffect(() => {
    setTableWidth(tableRef.current?.getBoundingClientRect().width);
  }, []);

  const [containerWidth, setContainerWidth] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    setTimeout(() => setContainerWidth(containerRef.current?.getBoundingClientRect().width), 100);
    const resizeHandler = () => setContainerWidth(containerRef.current?.getBoundingClientRect().width);
    window.addEventListener("resize", resizeHandler);
    return () => window.removeEventListener("resize", resizeHandler);
  }, []);

  const router = useRouter();
  const checkRowOpen = (row) => router.asPath.includes(`?open=${row.original.id}`);

  const Heading = showHeading && (
    <SectionHeading title={headingTitle}>
      <div className="flex">
        {allowColumnToggle && <ColumnToggle allColumns={allColumns} />}
        {HeadActions && HeadActions}
      </div>
    </SectionHeading>
  );
  if (!data.length && noDataComponent) {
    return (
      <>
        {Heading}
        <p className="text-xl">{noDataComponent}</p>
      </>
    );
  }
  return (
    <>
      {Heading}
      {!virtualized && <Pagination {...tableInstance} />}
      <div className="flex flex-col max-h-screen-80">
        <div className="overflow-x-auto border-b border-gray-200 shadow sm:rounded-lg" ref={containerRef}>
          <table className="min-w-full divide-y divide-gray-200" {...getTableProps()} ref={tableRef}>
            <thead className="bg-gray-50">
              {headerGroups.map((headerGroup) => (
                <tr
                  key={headerGroup.index}
                  className={cn({ flex: virtualized })}
                  {...headerGroup.getHeaderGroupProps()}
                >
                  {headerGroup.headers.map(
                    (column: HeaderGroup<any> & UseSortByColumnProps<any> & UseFiltersColumnProps<any>) => {
                      if (["Actions", "Expander"].includes(column.id)) {
                        return (
                          <th
                            key={column.id}
                            className="box-content flex-shrink-0 w-5 px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase align-top border-l first:border-none"
                          >
                            <span className="sr-only">Actions</span>
                          </th>
                        );
                      }

                      return (
                        <th
                          key={column.id}
                          className="flex-shrink-0 px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase align-top border-l first:border-none"
                          {...column.getHeaderProps(column.getSortByToggleProps())}
                        >
                          <div className="relative flex">
                            <p className="pr-6 truncate ">{column.render("Header")}</p>
                            {column.canSort &&
                              (column.isSorted ? (
                                column.isSortedDesc ? (
                                  <SortDescending className="absolute top-0 right-0 w-5 h-5 text-gray-400" />
                                ) : (
                                  <SortAscending className="absolute top-0 right-0 w-5 h-5 text-gray-400" />
                                )
                              ) : (
                                <SwitchVertical className="absolute top-0 right-0 w-5 h-5 text-gray-400" />
                              ))}
                          </div>
                          {withFilter && (
                            <div className="mt-2 ">{column.canFilter ? column.render("Filter") : null}</div>
                          )}
                        </th>
                      );
                    }
                  )}
                </tr>
              ))}
            </thead>
            <tbody className="bg-white divide-y divide-gray-200" {...getTableBodyProps()}>
              {virtualized ? (
                <FixedSizeList
                  height={window.innerHeight * 0.75}
                  itemCount={rows.length}
                  itemSize={100}
                  width={tableWidth}
                  style={{ overflowX: "hidden" }}
                >
                  {({ index, style }) => (
                    <ReactTableRow
                      key={rows[index].id}
                      prepareRow={prepareRow}
                      row={rows[index]}
                      isOpen={checkRowOpen(rows[index])}
                      style={{ ...style, display: "flex", alignItems: "center" }}
                      SubComponent={SubComponent}
                      SubComponentWidth={containerWidth}
                    />
                  )}
                </FixedSizeList>
              ) : (
                page.map((row) => (
                  <ReactTableRow
                    key={row.id}
                    prepareRow={prepareRow}
                    row={row}
                    isOpen={checkRowOpen(row)}
                    SubComponent={SubComponent}
                    SubComponentWidth={containerWidth}
                  />
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
};
